/**
 * MessageRenderer — single entry point for rendering ANY WhatsApp message
 * bubble body, shared by the institute inbox and the super-admin inbox.
 *
 * Given a normalised message (as serialised by the backend), it picks the
 * right per-type component. Unknown / future types fall back to
 * UnsupportedMessage and NEVER crash the chat view.
 *
 * Props:
 *   message       — { _id, messageType, content, mediaId, mediaUrl,
 *                     mediaMimeType, mediaFilename, metadata, ... }
 *   mediaEndpoint — backend proxy base for lazy media fetch
 *                   (institute: "/whatsapp/media", platform: "/platform/whatsapp/media")
 */
import React from "react";
import ErrorBoundary from "./MessageErrorBoundary";
import TextMessage from "./TextMessage";
import LocationMessage from "./LocationMessage";
import ContactMessage from "./ContactMessage";
import ReactionMessage from "./ReactionMessage";
import InteractiveMessage from "./InteractiveMessage";
import UnsupportedMessage from "./UnsupportedMessage";
import DeletedMessage from "./DeletedMessage";
import {
  ImageMessage, StickerMessage, VideoMessage, AudioMessage, DocumentMessage,
} from "./MediaMessage";
import "./messages.scss";

const MEDIA_TYPES = new Set(["image", "sticker", "video", "audio", "document"]);

// A media bubble may also carry a text caption we want to show beneath it.
const captionWorthShowing = (message) => {
  const c = (message.content || "").trim();
  if (!c) return false;
  // Skip placeholder strings produced by older parser versions, e.g. "[image]".
  return !/^\[(image|sticker|video|audio|document|location|contacts|interactive|reaction|button|order|system|unsupported|unknown)\]$/i.test(c);
};

const renderByType = (message, mediaEndpoint) => {
  const type = message.messageType;

  if (MEDIA_TYPES.has(type)) {
    const Media = {
      image: ImageMessage,
      sticker: StickerMessage,
      video: VideoMessage,
      audio: AudioMessage,
      document: DocumentMessage,
    }[type];
    return (
      <>
        <Media message={message} mediaEndpoint={mediaEndpoint} />
        {captionWorthShowing(message) && (
          <span className="wa-msg__text wa-msg__caption">{message.content}</span>
        )}
      </>
    );
  }

  switch (type) {
    case "text":
    case "template":
      return <TextMessage message={message} />;
    case "location":
      return <LocationMessage message={message} />;
    case "contacts":
      return <ContactMessage message={message} />;
    case "reaction":
      return <ReactionMessage message={message} />;
    case "interactive":
    case "button":
      return <InteractiveMessage message={message} />;
    // order / system / unknown / unsupported / anything new from Meta
    default:
      return <UnsupportedMessage message={message} />;
  }
};

const MessageRenderer = ({ message, mediaEndpoint = "/whatsapp/media" }) => {
  if (!message) return null;
  // Soft-deleted messages render as a tombstone regardless of original type.
  if (message.deleted) return <DeletedMessage message={message} />;
  // ErrorBoundary guarantees a single malformed payload can't blank the chat.
  return (
    <ErrorBoundary fallback={<UnsupportedMessage message={message} />}>
      {renderByType(message, mediaEndpoint)}
    </ErrorBoundary>
  );
};

export default MessageRenderer;
